import { toast } from "../common_components/Toast/ToastUtils";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
export const getErrorMsg = (res, defaultMsg) => {
    const responseData = res?.response?.data || res?.data;
    if (responseData?.message) {
        if (typeof responseData.message === 'string')
            return responseData.message;
        if (typeof responseData.message === 'object') {
            const parts = [];
            if (responseData.message.error)
                parts.push(String(responseData.message.error));
            if (responseData.message.description)
                parts.push(String(responseData.message.description));
            return parts.length > 0 ? parts.join(" - ") : defaultMsg;
        }
    }
    if (Array.isArray(responseData?.data) && typeof responseData.data[0] === 'string') {
        return responseData.data[0];
    }
    else if (typeof responseData?.data === 'string') {
        return responseData.data;
    }
    return defaultMsg;
};
import { clearCache } from "../helper/cache";
import { ApiCall } from "../helper/axios";
import { PAID_MEDIA_NETWORKS as CONST_PAID, AFFILIATE_NETWORKS as CONST_AFFILIATE } from "../utils/networkConstants";
/* ================= INITIAL STATE ================= */
const initialState = {
    clientsById: {},
    clientIds: [],
    clientsLoading: false,
    clientsError: null,
    connectionsByClientId: {},
    oldConnectionsByClientId: {},
    connectionsLoading: false,
    accountManagers: [],
    accountManagersLoading: false,
    operators: [],
    operatorsLoading: false,
    counts: { active: 0, inactive: 0, total: 0 },
    pagination: { page: 1, totalPages: 1, totalEntries: 0 },
    logsLoading: false,
    logsData: null,
    isSavingClient: false,
    networkAccounts: [],
    networkAccountsLoading: false,
    networkAccountsSyncing: false,
    // Connection Actions
    connectionActionLoading: false,
    // Shopify/Klaviyo
    shopifyConnecting: false,
    klaviyoConnecting: false,
    filterDeleting: false,
    // Affiliate Networks
    affiliateSaving: false,
    affiliateAccountDeleting: false,
    // NEW: Client List (new API)
    clientList: [],
    clientListLoading: false,
    clientListError: null,
    clientListRequestId: null,
    // NEW: Designation Wise Users
    designationWiseUsers: null,
    designationUsersLoading: false,
    // 👇 new
    emailCheckLoading: false,
    emailCheckError: null,
    emailExists: null,
    networkAccountsPagination: null,
    allClientList: [],
    allClientListLoading: false,
    allClientListError: null,
    affiliateStatusUpdating: false, // ✅ ADD THIS
    klaviyoDeleting: false,
    connectionDeleting: false, // ✅ ADD THIS
};
/* ================= STATIC NETWORK DEFINITIONS ================= */
export const PAID_MEDIA_NETWORKS = CONST_PAID.map(n => ({
    id: n.name,
    name: n.label,
    icon: n.icon,
    connected: false,
    accounts: 0
}));
export const AFFILIATE_NETWORKS = CONST_AFFILIATE.map(n => ({
    id: n.name,
    name: n.label,
    icon: n.icon,
    connected: false,
    accounts: 0
}));
export const AFFILIATE_NETWORK_ORDER = ['avantlink', 'awin', 'cj', 'commission_factory', 'costingtons', 'impact', 'levanta', 'partnerize', 'partnerstack', 'pepperjam', 'rakuten', 'refersion'];
export const fetchAllClientList = createAsyncThunk("clientData/fetchAllClientList", async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("GET", `/api/client-store/list-all`, // ✅ no query params
        "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.status === 200 && res.data?.success) {
            return res.data.data;
        }
        return rejectWithValue(res?.data?.message || "Failed to fetch all clients");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Fetch all clients error");
    }
});
/* ================= THUNKS ================= */
export const fetchClientList = createAsyncThunk("clientData/fetchClientList", async ({ params }, { getState, rejectWithValue, signal }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("GET", `/api/client-store/list?${params.toString()}`, "", { Authorization: `Bearer ${token}` }, undefined, "json", signal);
        if (signal.aborted) {
            return rejectWithValue("Request aborted");
        }
        if (res && "data" in res && res.status === 200 && res.data?.success) {
            return res.data.data;
        }
        return rejectWithValue(res?.data?.message || "Failed to fetch client list");
    }
    catch (err) {
        if (signal.aborted) {
            return rejectWithValue("Request aborted");
        }
        return rejectWithValue(err?.message || "Fetch client list error");
    }
});
/* -------- FETCH DESIGNATION WISE USERS -------- */
export const fetchDesignationWiseUsers = createAsyncThunk("clientData/fetchDesignationWiseUsers", async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("GET", `/api/utility/designationwise-user-list`, "", { Authorization: `Bearer ${token}` });
        // ✅ FIX: Add proper type checking
        if (res && "data" in res && "status" in res && res.status === 200 && res.data?.success) {
            const rawData = res.data.data || [];
            const designationMap = {};
            const allUsers = [];
            let accountManagers = [];
            rawData.forEach((item) => {
                const designation = Object.keys(item)[0];
                const users = item[designation] || [];
                designationMap[designation] = users;
                allUsers.push(...users);
                if (designation === "Account Manager") {
                    accountManagers = users;
                }
            });
            return {
                accountManagers,
                allUsers,
                designationMap
            };
        }
        return rejectWithValue(res?.data?.message || "Failed to fetch designation wise users");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Fetch designation wise users error");
    }
});
/* -------- DELETE CONNECTION (New API) -------- */
export const deleteConnectionById = createAsyncThunk("clientData/deleteConnectionById", async ({ connectionId }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/delete-connection`, { connectionId }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(res.data?.message || "Connection deleted successfully!");
            return { connectionId };
        }
        const msg = res?.message || "Failed to delete connection";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Delete connection error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- ACTIVATE CLIENT -------- */
export const activateClient = createAsyncThunk("clientData/activateClient", async ({ clientId }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("DELETE", `/api/client-store/${clientId}`, { action: "reactivate" }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success("Client activated successfully!");
            // This endpoint might also return the updated client or just success
            // But we optimistic update so only if success is true
            return clientId;
        }
        const msg = getErrorMsg(res, "Failed to activate client");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Activate client error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- REMOVE CLIENT -------- */
export const removeClient = createAsyncThunk("clientData/removeClient", async ({ clientId, type }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("DELETE", `/api/client-store/${clientId}`, { type }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            let successMsg = "Client moved to inactive list";
            if (type === "permanent")
                successMsg = "Client deleted permanently";
            if (type === "wipe")
                successMsg = "Client wiped out successfully";
            toast.success(successMsg);
            return { clientId, type };
        }
        const msg = getErrorMsg(res, "Failed to remove client");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Remove client error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- INVITE CLIENT -------- */
export const inviteClient = createAsyncThunk("clientData/inviteClient", async ({ contactId }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/client-store/contacts/${contactId}/reset-password`, "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success('Invitation sent successfully!');
            return true;
        }
        const msg = getErrorMsg(res, "Failed to invite client");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Invite client error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- FETCH CLIENT DETAILS -------- */
export const fetchClientDetails = createAsyncThunk("clientData/fetchClientDetails", async ({ clientId }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("GET", `/api/client-store/${clientId}`, "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            return res.data.data;
        }
        toast.warning("Failed to fetch client details");
        return rejectWithValue("Failed to fetch client details");
    }
    catch (err) {
        toast.warning(err?.message || "Fetch client details error");
        return rejectWithValue(err?.message || "Fetch client details error");
    }
});
export const checkEmailExists = createAsyncThunk("clientData/checkEmailExists", async ({ email, _id }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const params = new URLSearchParams();
        if (_id)
            params.append("_id", _id);
        const res = await ApiCall("GET", `/api/client-store/check-email/${encodeURIComponent(email)}${params.toString() ? `?${params.toString()}` : ""}`, "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res) {
            const data = res.data;
            const exists = data?.data?.exists === true;
            const message = data?.message;
            return { exists, message };
        }
        return { exists: false };
    }
    catch (err) {
        return rejectWithValue(err?.message || "Check email error");
    }
});
/* -------- FETCH CLIENT CONNECTIONS (SINGLE) -------- */
export const fetchClientConnections = createAsyncThunk("clientData/fetchClientConnections", async ({ clientId, network }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const url = network
            ? `/api/client-store/connections/${clientId}/${network}`
            : `/api/client-store/connections/${clientId}`;
        const res = await ApiCall("GET", url, "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            return {
                clientId,
                network,
                connections: res.data.data || [],
                old_entries: res.data.old_entries || []
            };
        }
        return {
            clientId,
            network,
            connections: [],
            old_entries: []
        };
    }
    catch (err) {
        return rejectWithValue(err?.message || "Fetch connections error");
    }
});
/* -------- FETCH CLIENT LOGS -------- */
export const fetchClientLogs = createAsyncThunk("clientData/fetchClientLogs", async ({ clientId, params }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("GET", `/api/client-store/logs/change-logs/${clientId}?${params.toString()}`, "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            return res.data.data;
        }
        return rejectWithValue("Failed to fetch logs");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Fetch logs error");
    }
});
/* -------- FETCH ACCOUNT HANDLERS -------- */
export const fetchClientAccountHandlers = createAsyncThunk("clientData/fetchClientAccountHandlers", async ({ clientId, searchText }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        let url = `/api/client-store/account-handlers/${clientId}`;
        if (searchText) {
            url += `?searchText=${encodeURIComponent(searchText)}`;
        }
        const res = await ApiCall("GET", url, "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            return res.data;
        }
        return rejectWithValue("Failed to fetch account handlers");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Fetch account handlers error");
    }
});
/* -------- DELETE CONTACT -------- */
export const deleteContact = createAsyncThunk("clientData/deleteContact", async ({ contactId }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("DELETE", `/api/client-store/contacts/${contactId}`, "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success("Contact deleted successfully");
            return contactId;
        }
        const msg = getErrorMsg(res, "Failed to delete contact");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Delete contact error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- RESET CONTACT PASSWORD (INVITE) -------- */
export const resetContactPassword = createAsyncThunk("clientData/resetContactPassword", async ({ contactId }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/client-store/contacts/${contactId}/reset-password`, "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(res.data.message || "Invitation sent successfully!");
            return true;
        }
        const msg = getErrorMsg(res, "Failed to send invitation");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = "Failed to send invitation";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CREATE CLIENT -------- */
export const createClient = createAsyncThunk("clientData/createClient", async ({ data }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", "/api/client-store", data, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success("Client created successfully!");
            return res.data;
        }
        const msg = getErrorMsg(res, "Failed to create client");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Create client error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- UPDATE CLIENT -------- */
export const updateClient = createAsyncThunk("clientData/updateClient", async ({ clientId, data }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("PUT", `/api/client-store/${clientId}`, data, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success("Client updated successfully!");
            // Return validation of success or the updated client data if available
            // Usually we'd want to return the updated structure to update the store
            return data;
        }
        const msg = getErrorMsg(res, "Failed to update client");
        throw new Error(msg);
    }
    catch (err) {
        const msg = err?.message || "Update client error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
export const toggleNetworkConnection = createAsyncThunk("clientData/toggleNetworkConnection", async ({ clientId, networkId, connected }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/client-store/bulk-disconnect`, { clientId, networkId, connected }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(connected ? "Network connected!" : "Network disconnected!");
            return { clientId, networkId, connected };
        }
        const msg = getErrorMsg(res, "Failed to toggle network");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Toggle network error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
export const fetchNetworkAccounts = createAsyncThunk("clientData/fetchNetworkAccounts", async ({ network, searchText, page = 1, limit = 10, clientId, sortBy, sortOrder }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const params = new URLSearchParams({
            network,
            page: page.toString(),
            clientId: clientId || "",
            limit: limit.toString()
        });
        if (searchText?.trim()) {
            params.append("searchText", searchText.trim());
        }
        if (sortBy) {
            params.append("sortBy", sortBy);
        }
        if (sortOrder) {
            params.append("sortOrder", sortOrder);
        }
        const res = await ApiCall("GET", `/api/network-accounts?${params.toString()}`, "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.status >= 200 && res.status < 300) {
            const responseData = res.data?.data;
            return {
                accounts: responseData?.accounts || [],
                pagination: responseData?.pagination || {
                    currentPage: 1,
                    pageSize: 10,
                    totalItems: 0,
                    totalPages: 1,
                    hasNextPage: false,
                    hasPrevPage: false
                }
            };
        }
        return rejectWithValue("Failed to fetch network accounts");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Fetch network accounts error");
    }
});
/* -------- SYNC NETWORK ACCOUNTS -------- */
export const syncNetworkAccounts = createAsyncThunk("clientData/syncNetworkAccounts", async ({ network }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/network-accounts/sync`, { network }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success("Accounts synced successfully!");
            return true;
        }
        const msg = getErrorMsg(res, "Failed to sync accounts");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Sync accounts error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT/DISCONNECT ACCOUNT -------- */
export const connectDisconnectAccount = createAsyncThunk("clientData/connectDisconnectAccount", async ({ clientId, network, accountId, action, accountName }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/network-accounts/${clientId}`, { network, account_id: accountId, action }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(`"${accountName}" ${action === "connect" ? "connected" : action === "disconnect" ? "disconnected" : "deleted"} successfully!`);
            return { accountId, action, accountName };
        }
        const msg = getErrorMsg(res, `Failed to ${action} account`);
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || `${action} account error`;
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- TOGGLE CONNECTION API ENABLED (accounts table) -------- */
export const setNetworkAccountApiEnabled = createAsyncThunk("clientData/setNetworkAccountApiEnabled", async ({ clientId, network, connectionId, api_enabled, accountName }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/network-accounts/${clientId}`, { network, action: "set_api", connection_id: connectionId, api_enabled }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(`API ${api_enabled ? "enabled" : "disabled"}${accountName ? ` for "${accountName}"` : ""}`);
            return { connectionId, api_enabled };
        }
        const msg = getErrorMsg(res, "Failed to update API status");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Update API status error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- TOGGLE API ENABLED FOR ALL NETWORKS OF A CLIENT -------- */
export const setClientNetworksApiEnabled = createAsyncThunk("clientData/setClientNetworksApiEnabled", async ({ clientId, api_enabled }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/network-accounts/${clientId}`, { action: "set_api_all", api_enabled }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            return { clientId, api_enabled };
        }
        const msg = getErrorMsg(res, "Failed to update networks API status");
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Update networks API status error";
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT SHOPIFY -------- */
export const connectShopify = createAsyncThunk("clientData/connectShopify", async ({ clientId, connectionId, storeUrl, accessToken, oauthClientId, oauthClientSecret, filters, shopifyRevenueSettings, returnPolicy, refreshData, isUpdateOnly, installation_link, api_enabled }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const payload = {
            connectionId,
            storeUrl,
            filters,
            shopify_revenue_settings: shopifyRevenueSettings,
            return_policy: returnPolicy,
            refreshData,
            isUpdateOnly,
            installation_link,
            api_enabled,
        };
        if (!isUpdateOnly) {
            if (accessToken)
                payload.accessToken = accessToken;
            if (oauthClientId)
                payload.oauth_client_id = oauthClientId;
            if (oauthClientSecret)
                payload.oauth_client_secret = oauthClientSecret;
        }
        const res = await ApiCall("POST", `/api/connect-shopify/${clientId}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            clearCache();
            toast.success(res.data.message || "Shopify connected successfully!");
            return {
                filters: res.data.data?.filter || []
            };
        }
        const resData = res && "data" in res ? res.data : {};
        const msg = resData?.message || getErrorMsg(res, "Failed to connect Shopify");
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect Shopify error";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
/* -------- SHOPIFY INSTALL URL -------- */
// export const fetchShopifyInstallUrl = createAsyncThunk<
//     { installation_link: string; connection_id: string },
//     { oauth_client_id: string; oauth_client_secret: string; shop: string; client_id: string; refreshData?: boolean },
//     { state: RootState; rejectValue: string }
// >(
//     "clientData/fetchShopifyInstallUrl",
//     async ({ oauth_client_id, oauth_client_secret, shop, client_id, refreshData }, { getState, rejectWithValue }) => {
//         try {
//             const token = getState().auth?.token;
//             const res = await ApiCall(
//                 "POST",
//                 `/api/shopify/oauth/install-url`,
//                 { oauth_client_id, oauth_client_secret, shop, client_id, refreshData },
//                 { Authorization: `Bearer ${token}` }
//             );
//             if (res && "data" in res && res.data?.installation_link) {
//                 return {
//                     installation_link: res.data.installation_link,
//                     connection_id: String(res.data.connection_id || ""),
//                 };
//             }
//             const msg = getErrorMsg(res, "Failed to get Shopify install URL");
//             toast.error(msg);
//             return rejectWithValue(msg);
//         } catch (err: any) {
//             const msg = getErrorMsg(err, "Failed to get Shopify install URL");
//             toast.error(msg);
//             return rejectWithValue(msg);
//         }
//     }
// );
/* -------- SEND SHOPIFY INSTALL EMAIL -------- */
export const sendShopifyInstallEmail = createAsyncThunk("clientData/sendShopifyInstallEmail", async ({ connection_id, emails }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/shopify/send-install-email`, { connection_id, emails }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            return;
        }
        return rejectWithValue(getErrorMsg(res, "Failed to send installation email"));
    }
    catch (err) {
        return rejectWithValue(getErrorMsg(err, "Failed to send installation email"));
    }
});
/* -------- CONNECT KLAVIYO -------- */
export const connectKlaviyo = createAsyncThunk("clientData/connectKlaviyo", async ({ clientId, accountKey, filters, refreshData, api_enabled }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-klaviyo/${clientId}`, { accountkey: accountKey, filters, refreshData, api_enabled }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(res.data.message || "Klaviyo connected successfully!");
            return {
                filters: res.data.data?.filter || []
            };
        }
        const msg = getErrorMsg(res, "Failed to connect Klaviyo");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect Klaviyo error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- DELETE SHOPIFY FILTER -------- */
export const deleteShopifyFilter = createAsyncThunk("clientData/deleteShopifyFilter", async ({ clientId, storeUrl, filterIndex }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("DELETE", `/api/shopify-filter/${clientId}`, { storeUrl, filterIndex }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success("Filter deleted successfully!");
            return { filterIndex };
        }
        const msg = getErrorMsg(res, "Failed to delete filter");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Delete filter error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- DELETE KLAVIYO FILTER -------- */
export const deleteKlaviyoFilter = createAsyncThunk("clientData/deleteKlaviyoFilter", async ({ clientId, accountKey, filterIndex }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("DELETE", `/api/klaviyo-filter/${clientId}`, { accountkey: accountKey, filterIndex }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success("Filter deleted successfully!");
            return { filterIndex };
        }
        const msg = getErrorMsg(res, "Failed to delete filter");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Delete filter error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- DELETE KLAVIYO CONNECTION -------- */
export const deleteKlaviyoConnection = createAsyncThunk("clientData/deleteKlaviyoConnection", async ({ clientId, accountKey }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("DELETE", `/api/klaviyo-filter/${clientId}`, { accountkey: accountKey, deleteConnection: true }, // ✅ NEW FLAG
        { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success("Klaviyo connection deleted successfully!");
            return true;
        }
        const msg = getErrorMsg(res, "Failed to delete Klaviyo connection");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Delete Klaviyo connection error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- DELETE CONNECTION (Orphaned cleanup) -------- */
export const deleteConnection = createAsyncThunk("clientData/deleteConnection", async ({ clientId, connectionId }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("DELETE", `/api/client-store/connections/${clientId}/${connectionId}`, "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success("Connection removed successfully!");
            return { connectionId, clientId };
        }
        const msg = getErrorMsg(res, "Failed to remove connection");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Delete connection error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
export const connectAvantLink = createAsyncThunk("clientData/connectAvantLink", async ({ clientId, payload, suppressToast }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-avantlink/${clientId}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            if (!suppressToast)
                toast.success(res.data.message || "AvantLink account connected successfully!");
            return true;
        }
        const msg = getErrorMsg(res, "Failed to connect AvantLink");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect AvantLink error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT AWIN (Single Account) -------- */
export const connectAwin = createAsyncThunk("clientData/connectAwin", async ({ clientId, payload, suppressToast }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-awin/${clientId}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            if (!suppressToast)
                toast.success(`Awin account "${payload.accountName}" connected successfully!`);
            return true;
        }
        const msg = getErrorMsg(res, `Failed to connect Awin account "${payload.accountName}"`);
        // Handle specific error codes
        if (res?.status === 401) {
            toast.warning("Invalid Awin credentials. Please check your Program ID and OAuth2 Token.");
            return rejectWithValue("Invalid credentials");
        }
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect Awin error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT CJ -------- */
export const connectCJ = createAsyncThunk("clientData/connectCJ", async ({ clientId, payload, suppressToast }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-cj/${clientId}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            if (!suppressToast)
                toast.success(res.data.message || "CJ account connected successfully!");
            return true;
        }
        const msg = getErrorMsg(res, "Failed to connect CJ account");
        // Handle specific error codes
        const status = res?.status;
        if (status === 400) {
            toast.warning("Client ID and Account ID are required.");
        }
        else if (status === 401) {
            toast.warning("Invalid CJ Account key");
        }
        else {
            toast.warning(msg);
        }
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect CJ error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT IMPACT (Single Account) -------- */
export const connectImpact = createAsyncThunk("clientData/connectImpact", async ({ clientId, payload, suppressToast }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-impact/${clientId}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            if (!suppressToast)
                toast.success(`Impact account "${payload.accountName}" connected successfully!`);
            return true;
        }
        const msg = getErrorMsg(res, `Failed to connect Impact account "${payload.accountName}"`);
        // Handle specific error codes
        if (res?.status === 401) {
            toast.warning("Invalid Impact credentials. Please check your Account ID and Auth Token.");
            return rejectWithValue("Invalid credentials");
        }
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect Impact error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT LEVANTA -------- */
export const connectLevanta = createAsyncThunk("clientData/connectLevanta", async ({ clientId, payload, suppressToast }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-levanta/${clientId}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            if (!suppressToast)
                toast.success(res.data.message || "Levanta account connected successfully!");
            return true;
        }
        const msg = getErrorMsg(res, "Failed to connect Levanta account");
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect Levanta error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT RAKUTEN -------- */
export const connectRakuten = createAsyncThunk("clientData/connectRakuten", async ({ clientId, payload, suppressToast }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-rakuten/${clientId}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            if (!suppressToast)
                toast.success(res.data.message || "Rakuten account connected successfully!");
            return true;
        }
        const msg = getErrorMsg(res, "Failed to connect Rakuten account");
        // Handle specific error codes
        if (res?.status === 401) {
            toast.warning("Invalid Rakuten credentials. Please check your Auth Token.");
            return rejectWithValue("Invalid credentials");
        }
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect Rakuten error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- DELETE AWIN ACCOUNT -------- */
export const deleteAwinAccount = createAsyncThunk("clientData/deleteAwinAccount", async ({ clientId, accountId, accountName }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("DELETE", `/api/awin-account/${clientId}/${accountId}`, "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(`Awin account "${accountName}" permanently deleted!`);
            return { accountId, accountName };
        }
        const msg = getErrorMsg(res, `Failed to delete Awin account "${accountName}"`);
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Delete Awin account error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- DELETE IMPACT ACCOUNT -------- */
export const deleteImpactAccount = createAsyncThunk("clientData/deleteImpactAccount", async ({ clientId, accountId, accountName }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("DELETE", `/api/impact-account/${clientId}/${accountId}`, "", { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(`Impact account "${accountName}" permanently deleted!`);
            return { accountId, accountName };
        }
        const msg = getErrorMsg(res, `Failed to delete Impact account "${accountName}"`);
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Delete Impact account error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* ================= THUNK ================= */
export const updateAffiliateAccountStatus = createAsyncThunk("clientData/updateAffiliateAccountStatus", async ({ clientId, networkType, accountId, status, accountName }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        // Create the payload exactly as backend expects
        const payload = {
            clientId,
            networkType,
            accountId,
            status,
        };
        const res = await ApiCall("POST", `/api/affiliate-account/status`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            const action = status === "active" ? "activated" : "deactivated";
            toast.success(`${accountName} ${action} successfully!`);
            // Return all required fields including clientId
            return {
                clientId,
                accountId,
                status,
                networkType,
                accountName
            };
        }
        // Handle error response from backend
        const errorMessage = res?.data?.message ||
            res?.message ||
            "Failed to update account status";
        toast.warning(errorMessage);
        return rejectWithValue(errorMessage);
    }
    catch (err) {
        const msg = err?.message || "Update account status error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT PEPPERJAM -------- */
export const connectPepperjam = createAsyncThunk("clientData/connectPepperjam", async ({ clientId, payload, suppressToast }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-pepperjam/${clientId}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            if (!suppressToast)
                toast.success(res.data.message || `Pepperjam account "${payload.accountName}" connected successfully!`);
            return true;
        }
        const msg = getErrorMsg(res, `Failed to connect Pepperjam account "${payload.accountName}"`);
        // Handle specific error codes
        if (res?.status === 401) {
            toast.warning("Invalid Pepperjam credentials. Please check your API Key.");
            return rejectWithValue("Invalid credentials");
        }
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect Pepperjam error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT REFERSION -------- */
export const connectRefersion = createAsyncThunk("clientData/connectRefersion", async ({ clientId, payload, suppressToast }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-refersion/${clientId}`, payload, // ✅ Will now send { authToken, accountName }
        { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            if (!suppressToast)
                toast.success(res.data.message || `Refersion account "${payload.accountName}" connected successfully!`);
            return true;
        }
        const msg = getErrorMsg(res, `Failed to connect Refersion account "${payload.accountName}"`);
        if (res?.status === 401) {
            toast.warning("Invalid Refersion credentials. Please check your Auth Token.");
            return rejectWithValue("Invalid credentials");
        }
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect Refersion error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT COSTINGTONS -------- */
export const connectCostingtons = createAsyncThunk("clientData/connectCostingtons", async ({ clientId, payload, suppressToast }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-costingtons/${clientId}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            if (!suppressToast)
                toast.success(res.data.message || `Costingtons account "${payload.accountName}" connected successfully!`);
            return true;
        }
        const msg = getErrorMsg(res, `Failed to connect Costingtons account "${payload.accountName}"`);
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect Costingtons error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT COMMISSION FACTORY -------- */
export const connectCommissionFactory = createAsyncThunk("clientData/connectCommissionFactory", async ({ clientId, payload, suppressToast }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-commission-factory/${clientId}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            if (!suppressToast)
                toast.success(res.data.message || `Commission Factory account "${payload.accountName}" connected successfully!`);
            return true;
        }
        const msg = getErrorMsg(res, `Failed to connect Commission Factory account "${payload.accountName}"`);
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect Commission Factory error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT PARTNERSTACK -------- */
export const connectPartnerstack = createAsyncThunk("clientData/connectPartnerstack", async ({ clientId, payload, suppressToast }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-partnerstack/${clientId}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            if (!suppressToast)
                toast.success(res.data.message || `Partnerstack account "${payload.accountName}" connected successfully!`);
            return true;
        }
        const msg = getErrorMsg(res, `Failed to connect Partnerstack account "${payload.accountName}"`);
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect Partnerstack error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* -------- CONNECT PARTNERIZE -------- */
export const connectPartnerize = createAsyncThunk("clientData/connectPartnerize", async ({ clientId, payload, suppressToast }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/connect-partnerize/${clientId}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            if (!suppressToast)
                toast.success(res.data.message || `Partnerize account "${payload.accountName}" connected successfully!`);
            return true;
        }
        const msg = getErrorMsg(res, `Failed to connect Partnerize account "${payload.accountName}"`);
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Connect Partnerize error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
/* ================= SLICE ================= */
const clientDataSlice = createSlice({
    name: "clientData",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            /* ACTIONS OPTIMISTIC UPDATES */
            .addCase(activateClient.fulfilled, (state, action) => {
            const clientId = action.payload;
            // Remove from current list (as it moved from Inactive -> Active)
            state.clientIds = state.clientIds.filter(id => id !== clientId);
            if (state.clientsById[clientId]) {
                state.clientsById[clientId].clientDetails.status = "active";
                // Update counts
                state.counts.active += 1;
                state.counts.inactive = Math.max(0, state.counts.inactive - 1);
                // Update pagination
                state.pagination.totalEntries = Math.max(0, state.pagination.totalEntries - 1);
            }
        })
            .addCase(removeClient.fulfilled, (state, action) => {
            const { clientId, type } = action.payload;
            // Remove from current list (either Permanently deleted OR moved Active -> Inactive)
            state.clientIds = state.clientIds.filter(id => id !== clientId);
            state.pagination.totalEntries = Math.max(0, state.pagination.totalEntries - 1);
            if (type === "permanent") {
                delete state.clientsById[clientId];
                state.counts.inactive = Math.max(0, state.counts.inactive - 1);
            }
            else {
                // Soft delete -> move to inactive
                if (state.clientsById[clientId]) {
                    state.clientsById[clientId].clientDetails.status = "inactive";
                    state.counts.active = Math.max(0, state.counts.active - 1);
                    state.counts.inactive += 1;
                }
            }
        })
            /* CONNECTIONS (SINGLE) */
            .addCase(fetchClientConnections.pending, (state) => {
            state.connectionsLoading = true;
        })
            .addCase(fetchClientConnections.rejected, (state) => {
            state.connectionsLoading = false;
        })
            .addCase(fetchClientConnections.fulfilled, (state, action) => {
            state.connectionsLoading = false;
            const { clientId, network, connections, old_entries } = action.payload;
            if (!state.connectionsByClientId[clientId]) {
                state.connectionsByClientId[clientId] = {};
            }
            if (!state.oldConnectionsByClientId[clientId]) {
                state.oldConnectionsByClientId[clientId] = {};
            }
            if (network) {
                state.connectionsByClientId[clientId][network] = connections;
                state.oldConnectionsByClientId[clientId][network] = old_entries || [];
            }
            else {
                state.connectionsByClientId[clientId]["all"] = connections;
                state.oldConnectionsByClientId[clientId]["all"] = old_entries || [];
            }
        })
            /* LOGS */
            .addCase(fetchClientLogs.pending, (state) => {
            state.logsLoading = true;
        })
            .addCase(fetchClientLogs.fulfilled, (state, action) => {
            state.logsLoading = false;
            state.logsData = action.payload;
        })
            .addCase(fetchClientLogs.rejected, (state) => {
            state.logsLoading = false;
        })
            /* ACCOUNT HANDLERS */
            .addCase(fetchClientAccountHandlers.fulfilled, (state, action) => {
            // We might want to store this if valuable, for now just returning payload for component use
            // or we could add a state property for currentClientAccountHandlers
        })
            /* CREATE / UPDATE */
            .addCase(createClient.pending, (state) => {
            state.isSavingClient = true;
        })
            .addCase(createClient.fulfilled, (state, action) => {
            state.isSavingClient = false;
        })
            .addCase(createClient.rejected, (state) => {
            state.isSavingClient = false;
        })
            .addCase(updateClient.pending, (state) => {
            state.isSavingClient = true;
        })
            .addCase(updateClient.fulfilled, (state, action) => {
            state.isSavingClient = false;
            // Update the specific client in state
            const updatedClient = action.payload;
            const id = updatedClient.clientDetails?._id;
            if (id && state.clientsById[id]) {
                state.clientsById[id] = updatedClient;
            }
        })
            .addCase(updateClient.rejected, (state) => {
            state.isSavingClient = false;
        })
            .addCase(toggleNetworkConnection.fulfilled, (state, action) => {
            const { clientId, networkId, connected } = action.payload;
            if (!connected) {
                // Remove connections for this network from both keyed and "all" cache
                if (state.connectionsByClientId[clientId]?.[networkId]) {
                    delete state.connectionsByClientId[clientId][networkId];
                }
                if (state.connectionsByClientId[clientId]?.["all"]) {
                    state.connectionsByClientId[clientId]["all"] =
                        state.connectionsByClientId[clientId]["all"].filter((conn) => conn.name?.toLowerCase() !== networkId?.toLowerCase());
                }
            }
        })
            .addCase(updateAffiliateAccountStatus.pending, (state) => {
            state.affiliateStatusUpdating = true;
        })
            .addCase(updateAffiliateAccountStatus.fulfilled, (state, action) => {
            state.affiliateStatusUpdating = false;
            // Update local state with the returned data
            const { clientId, networkType, accountId, status } = action.payload;
            // Update in connectionsByClientId
            if (state.connectionsByClientId[clientId]?.[networkType]) {
                const accountIndex = state.connectionsByClientId[clientId][networkType].findIndex(conn => conn._id === accountId);
                if (accountIndex !== -1) {
                    state.connectionsByClientId[clientId][networkType][accountIndex].status = status;
                }
            }
            // Also update in our local accounts state if needed
            // This depends on how you're managing state in your component
        })
            .addCase(updateAffiliateAccountStatus.rejected, (state) => {
            state.affiliateStatusUpdating = false;
        })
            .addCase(fetchNetworkAccounts.pending, (state) => {
            state.networkAccountsLoading = true;
        })
            .addCase(fetchNetworkAccounts.fulfilled, (state, action) => {
            state.networkAccountsLoading = false;
            state.networkAccounts = action.payload.accounts;
            state.networkAccountsPagination = action.payload.pagination;
        })
            .addCase(fetchNetworkAccounts.rejected, (state) => {
            state.networkAccountsLoading = false;
            state.networkAccountsPagination = null;
        })
            /* SYNC ACCOUNTS */
            .addCase(syncNetworkAccounts.pending, (state) => {
            state.networkAccountsSyncing = true;
        })
            .addCase(syncNetworkAccounts.fulfilled, (state) => {
            state.networkAccountsSyncing = false;
        })
            .addCase(syncNetworkAccounts.rejected, (state) => {
            state.networkAccountsSyncing = false;
        })
            /* CONNECT/DISCONNECT ACCOUNT */
            .addCase(connectDisconnectAccount.pending, (state) => {
            state.connectionActionLoading = true;
        })
            .addCase(connectDisconnectAccount.fulfilled, (state) => {
            state.connectionActionLoading = false;
        })
            .addCase(connectDisconnectAccount.rejected, (state) => {
            state.connectionActionLoading = false;
        })
            /* SHOPIFY */
            .addCase(connectShopify.pending, (state) => {
            state.shopifyConnecting = true;
        })
            .addCase(connectShopify.fulfilled, (state) => {
            state.shopifyConnecting = false;
        })
            .addCase(connectShopify.rejected, (state) => {
            state.shopifyConnecting = false;
        })
            // .addCase(fetchShopifyInstallUrl.pending, (state) => {
            //     state.shopifyConnecting = true;
            // })
            // .addCase(fetchShopifyInstallUrl.fulfilled, (state) => {
            //     state.shopifyConnecting = false;
            // })
            // .addCase(fetchShopifyInstallUrl.rejected, (state) => {
            //     state.shopifyConnecting = false;
            // })
            /* KLAVIYO */
            .addCase(connectKlaviyo.pending, (state) => {
            state.klaviyoConnecting = true;
        })
            .addCase(connectKlaviyo.fulfilled, (state) => {
            state.klaviyoConnecting = false;
        })
            .addCase(connectKlaviyo.rejected, (state) => {
            state.klaviyoConnecting = false;
        })
            /* DELETE FILTERS */
            .addCase(deleteShopifyFilter.pending, (state) => {
            state.filterDeleting = true;
        })
            .addCase(deleteShopifyFilter.fulfilled, (state) => {
            state.filterDeleting = false;
        })
            .addCase(deleteShopifyFilter.rejected, (state) => {
            state.filterDeleting = false;
        })
            .addCase(deleteKlaviyoFilter.pending, (state) => {
            state.filterDeleting = true;
        })
            .addCase(deleteKlaviyoFilter.fulfilled, (state) => {
            state.filterDeleting = false;
        })
            .addCase(deleteKlaviyoFilter.rejected, (state) => {
            state.filterDeleting = false;
        })
            /* DELETE CONNECTION */
            .addCase(deleteConnection.fulfilled, (state, action) => {
            const { clientId, connectionId } = action.payload;
            Object.keys(state.connectionsByClientId[clientId] || {}).forEach(network => {
                state.connectionsByClientId[clientId][network] =
                    state.connectionsByClientId[clientId][network].filter(conn => conn._id !== connectionId);
            });
        })
            /* AFFILIATE CONNECT - All use same loading state */
            .addCase(connectAvantLink.pending, (state) => {
            state.affiliateSaving = true;
        })
            .addCase(connectAvantLink.fulfilled, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectAvantLink.rejected, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectAwin.pending, (state) => {
            state.affiliateSaving = true;
        })
            .addCase(connectAwin.fulfilled, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectAwin.rejected, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectCJ.pending, (state) => {
            state.affiliateSaving = true;
        })
            .addCase(connectCJ.fulfilled, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectCJ.rejected, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectImpact.pending, (state) => {
            state.affiliateSaving = true;
        })
            .addCase(connectImpact.fulfilled, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectImpact.rejected, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectLevanta.pending, (state) => {
            state.affiliateSaving = true;
        })
            .addCase(connectLevanta.fulfilled, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectLevanta.rejected, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectRakuten.pending, (state) => {
            state.affiliateSaving = true;
        })
            .addCase(connectRakuten.fulfilled, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectRakuten.rejected, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectPartnerstack.pending, (state) => {
            state.affiliateSaving = true;
        })
            .addCase(connectPartnerstack.fulfilled, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectPartnerstack.rejected, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectPartnerize.pending, (state) => {
            state.affiliateSaving = true;
        })
            .addCase(connectPartnerize.fulfilled, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectPartnerize.rejected, (state) => {
            state.affiliateSaving = false;
        })
            /* DELETE AFFILIATE ACCOUNTS */
            .addCase(deleteAwinAccount.pending, (state) => {
            state.affiliateAccountDeleting = true;
        })
            .addCase(deleteAwinAccount.fulfilled, (state) => {
            state.affiliateAccountDeleting = false;
        })
            .addCase(deleteAwinAccount.rejected, (state) => {
            state.affiliateAccountDeleting = false;
        })
            .addCase(deleteImpactAccount.pending, (state) => {
            state.affiliateAccountDeleting = true;
        })
            .addCase(deleteImpactAccount.fulfilled, (state) => {
            state.affiliateAccountDeleting = false;
        })
            .addCase(deleteImpactAccount.rejected, (state) => {
            state.affiliateAccountDeleting = false;
        })
            // CLIENT LIST (NEW API)
            .addCase(fetchClientList.pending, (state, action) => {
            state.clientListLoading = true;
            state.clientListError = null;
            state.clientListRequestId = action.meta.requestId;
        })
            .addCase(fetchClientList.fulfilled, (state, action) => {
            if (state.clientListRequestId !== action.meta.requestId) {
                return;
            }
            state.clientListLoading = false;
            state.clientList = action.payload.clients || [];
            state.counts = {
                active: action.payload.active || 0,
                inactive: action.payload.inactive || 0,
                total: action.payload.total || 0,
            };
            // Use active/inactive counts for pagination based on filter
            const params = action.meta.arg.params;
            const status = params.get("status");
            const searchText = params.get("searchText");
            let actualTotal = action.payload.total || 0;
            // If no search text, trust the category counts (active/inactive)
            if (!searchText) {
                if (status === "active")
                    actualTotal = action.payload.active || 0;
                else if (status === "inactive")
                    actualTotal = action.payload.inactive || 0;
            }
            const limit = Number(params.get("limit") || 10);
            const totalPages = Math.ceil(actualTotal / limit) || 1;
            state.pagination = {
                page: action.payload.page || 1,
                totalPages: totalPages,
                totalEntries: actualTotal,
            };
        })
            .addCase(fetchClientList.rejected, (state, action) => {
            if (action.meta.aborted) {
                return;
            }
            if (state.clientListRequestId === action.meta.requestId) {
                state.clientListLoading = false;
                state.clientListError = action.payload || null;
            }
        })
            .addCase(fetchAllClientList.pending, (state) => {
            state.allClientListLoading = true;
            state.allClientListError = null;
        })
            .addCase(fetchAllClientList.fulfilled, (state, action) => {
            state.allClientListLoading = false;
            state.allClientList = action.payload.clients || [];
        })
            .addCase(fetchAllClientList.rejected, (state, action) => {
            state.allClientListLoading = false;
            state.allClientListError = action.payload || null;
        })
            // DESIGNATION WISE USERS
            .addCase(fetchDesignationWiseUsers.pending, (state) => {
            state.designationUsersLoading = true;
        })
            .addCase(fetchDesignationWiseUsers.fulfilled, (state, action) => {
            state.designationUsersLoading = false;
            state.designationWiseUsers = action.payload;
            // Backward compatibility - update old state too
            state.accountManagers = action.payload.accountManagers;
            state.operators = action.payload.allUsers;
        })
            .addCase(fetchDesignationWiseUsers.rejected, (state) => {
            state.designationUsersLoading = false;
        })
            /* -------- CHECK EMAIL EXISTS -------- */
            .addCase(checkEmailExists.pending, (state) => {
            state.emailCheckLoading = true;
            state.emailCheckError = null;
            state.emailExists = null;
        })
            .addCase(checkEmailExists.fulfilled, (state, action) => {
            state.emailCheckLoading = false;
            state.emailExists = action.payload.exists;
        })
            .addCase(checkEmailExists.rejected, (state, action) => {
            state.emailCheckLoading = false;
            state.emailCheckError = action.payload || "Check email error";
            state.emailExists = null;
        })
            .addCase(deleteKlaviyoConnection.pending, (state) => {
            state.klaviyoDeleting = true;
        })
            .addCase(deleteKlaviyoConnection.fulfilled, (state) => {
            state.klaviyoDeleting = false;
        })
            .addCase(deleteKlaviyoConnection.rejected, (state) => {
            state.klaviyoDeleting = false;
        })
            .addCase(deleteConnectionById.pending, (state) => {
            state.connectionDeleting = true;
        })
            .addCase(deleteConnectionById.fulfilled, (state, action) => {
            state.connectionDeleting = false;
            const { connectionId } = action.payload;
            // Remove connection from all clients/networks
            Object.keys(state.connectionsByClientId).forEach(clientId => {
                Object.keys(state.connectionsByClientId[clientId] || {}).forEach(network => {
                    state.connectionsByClientId[clientId][network] =
                        state.connectionsByClientId[clientId][network].filter(conn => conn._id !== connectionId);
                });
            });
        })
            .addCase(deleteConnectionById.rejected, (state) => {
            state.connectionDeleting = false;
        })
            .addCase(connectPepperjam.pending, (state) => {
            state.affiliateSaving = true;
        })
            .addCase(connectPepperjam.fulfilled, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectPepperjam.rejected, (state) => {
            state.affiliateSaving = false;
        })
            // REFERSION
            .addCase(connectRefersion.pending, (state) => {
            state.affiliateSaving = true;
        })
            .addCase(connectRefersion.fulfilled, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectRefersion.rejected, (state) => {
            state.affiliateSaving = false;
        })
            // COSTINGTONS
            .addCase(connectCostingtons.pending, (state) => {
            state.affiliateSaving = true;
        })
            .addCase(connectCostingtons.fulfilled, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectCostingtons.rejected, (state) => {
            state.affiliateSaving = false;
        })
            // COMMISSION FACTORY
            .addCase(connectCommissionFactory.pending, (state) => {
            state.affiliateSaving = true;
        })
            .addCase(connectCommissionFactory.fulfilled, (state) => {
            state.affiliateSaving = false;
        })
            .addCase(connectCommissionFactory.rejected, (state) => {
            state.affiliateSaving = false;
        });
    },
});
/* ================= SELECTORS ================= */
// NEW SELECTORS
export const selectClientList = (state) => state.clientData.clientList;
export const selectClientListLoading = (state) => state.clientData.clientListLoading;
export const selectClientListError = (state) => state.clientData.clientListError;
export const selectDesignationWiseUsers = (state) => state.clientData.designationWiseUsers;
export const selectAccountManagersList = (state) => state.clientData.designationWiseUsers?.accountManagers || state.clientData.accountManagers || [];
export const selectAllUsersList = (state) => state.clientData.designationWiseUsers?.allUsers || state.clientData.operators || [];
export const selectUsersByDesignation = (designation) => (state) => state.clientData.designationWiseUsers?.designationMap[designation] || [];
export const selectAllClientList = (state) => state.clientData.allClientList;
export const selectClients = (state) => state.clientData.clientIds.map((id) => state.clientData.clientsById[id]);
export const selectClientsLoading = (state) => state.clientData.clientsLoading;
export const selectConnectionsByClient = (clientId, network) => (state) => network
    ? state.clientData.connectionsByClientId[clientId]?.[network] || []
    : state.clientData.connectionsByClientId[clientId]?.["all"] || [];
export const selectOldConnectionsByClient = (clientId, network) => (state) => network
    ? state.clientData.oldConnectionsByClientId[clientId]?.[network] || []
    : state.clientData.oldConnectionsByClientId[clientId]?.["all"] || [];
// Get networks with connection status for a client
export const selectNetworksWithStatus = (clientId, networkType, allowedNetworkIds = null) => (state) => {
    const clientNetworks = state.clientData.connectionsByClientId[clientId] || {};
    const apiNetworks = clientNetworks["all"] || [];
    // Create lookup map from API response
    const apiMap = {};
    apiNetworks.forEach((n) => {
        apiMap[n.name?.toLowerCase()] = {
            status: n.status,
            count: n.count,
            inactiveCount: n.inactiveCount || 0
        };
    });
    let baseNetworks = networkType === "paid_media"
        ? [...PAID_MEDIA_NETWORKS]
        : [...AFFILIATE_NETWORKS];
    if (allowedNetworkIds?.length) {
        baseNetworks = baseNetworks.filter(n => allowedNetworkIds.includes(n.id));
    }
    if (networkType === "affiliate") {
        baseNetworks.sort((a, b) => {
            const indexA = AFFILIATE_NETWORK_ORDER.indexOf(a.id);
            const indexB = AFFILIATE_NETWORK_ORDER.indexOf(b.id);
            return indexA - indexB;
        });
    }
    return baseNetworks.map(network => {
        const apiData = apiMap[network.id];
        return {
            ...network,
            connected: (apiData?.count || 0) > 0, // connected only when it has active connections
            accounts: apiData?.count || 0,
            inactiveAccounts: apiData?.inactiveCount || 0,
            status: apiData?.status || null
        };
    });
};
export const selectConnectionsLoading = (state) => state.clientData.connectionsLoading;
export const selectNetworkAccounts = (state) => state.clientData.networkAccounts;
export const selectNetworkAccountsLoading = (state) => state.clientData.networkAccountsLoading;
export const selectNetworkAccountsSyncing = (state) => state.clientData.networkAccountsSyncing;
export const selectShopifyConnecting = (state) => state.clientData.shopifyConnecting;
export const selectKlaviyoConnecting = (state) => state.clientData.klaviyoConnecting;
export const selectFilterDeleting = (state) => state.clientData.filterDeleting;
export const selectConnectionActionLoading = (state) => state.clientData.connectionActionLoading;
export const selectAffiliateSaving = (state) => state.clientData.affiliateSaving;
export const selectAffiliateAccountDeleting = (state) => state.clientData.affiliateAccountDeleting;
export const selectEmailCheckLoading = (state) => state.clientData.emailCheckLoading;
export const selectEmailExists = (state) => state.clientData.emailExists;
export const selectEmailCheckError = (state) => state.clientData.emailCheckError;
export const selectNetworkAccountsPagination = (state) => state.clientData.networkAccountsPagination;
export const selectAffiliateStatusUpdating = (state) => state.clientData.affiliateStatusUpdating;
export const selectKlaviyoDeleting = (state) => state.clientData.klaviyoDeleting;
export const selectConnectionDeleting = (state) => state.clientData.connectionDeleting;
export default clientDataSlice.reducer;
